import * as yup from 'yup';
import {
  ForwardedRef,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';
import {
  alertApiRef,
  configApiRef,
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InputBase, { InputBaseProps } from '@mui/material/InputBase';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { SxProps } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import { Score } from '@zxcvbn-ts/core';
import { isAxiosError } from 'axios';
import {
  useForm,
  UseFormRegisterReturn,
  useFormState,
  useWatch,
} from 'react-hook-form';
import ReCAPTCHA from 'react-google-recaptcha';
import { signup } from '../../api/signup';
import CotalityStarlings from '../../assets/images/cotality-starlings-bg.png';
import Alert from '../../assets/svg/alert-icon.svg';
import Visibility from '../../assets/svg/visibility.svg';
import VisibilityOff from '../../assets/svg/visibility-off.svg';
import { useEstimatePassword } from '../../hooks/useEstimatePassword';
import { colors } from '../../theme';

type ErrorMessageProps = {
  error?: string;
};

const ErrorMessage = ({ error }: ErrorMessageProps) => {
  return (
    <Stack direction="row" gap={1} alignItems="flex-start">
      {error && <img src={Alert} alt="Alert icon" style={{ marginTop: 1 }} />}
      <Typography variant="body2" color="#D5172F" fontSize={14}>
        {error}
      </Typography>
    </Stack>
  );
};

type PasswordVisibilityProps = {
  dataTestId: string;
  isVisible: boolean;
  toggleVisiblity: () => void;
};

const PasswordVisibility = ({
  dataTestId,
  isVisible,
  toggleVisiblity,
}: PasswordVisibilityProps) => {
  return (
    <InputAdornment position="end">
      <IconButton
        sx={{ p: 0.25 }}
        onClick={toggleVisiblity}
        tabIndex={-1}
        data-testid={dataTestId}
      >
        {isVisible ? (
          <img src={VisibilityOff} alt="Hide Password" />
        ) : (
          <img src={Visibility} alt="Show Password" />
        )}
      </IconButton>
    </InputAdornment>
  );
};

type TextFieldProps = Omit<InputBaseProps, 'error'> & {
  field: string;
  error?: string;
  passwordStrength?: Score | -1;
};

const TextFieldComponent = (
  { type = 'text', field, error, passwordStrength, ...props }: TextFieldProps,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const [showPassword, setShowPassword] = useState(false);

  const passwordStrengthColors = [
    '#d9534f',
    '#f0ad4e',
    '#f0ad4e',
    '#33b737',
    '#33b737',
  ];

  const passwordStrengthWidths = ['20%', '40%', '60%', '80%', '100%'];

  const inputStyles: SxProps = {
    px: 1.5,
    height: 40,
    fontSize: 14,
    borderRadius: 1,
    border: '1px solid rgba(0, 0, 0, 0.3)',
    '&.Mui-focused': {
      border: '1px solid rgba(0, 0, 0, 0.5)',
    },
    '&.Mui-error': {
      border: '1px solid #D5172F',
    },
  };

  return (
    <Stack width="100%" gap={1}>
      <Typography variant="body2" fontSize={14} fontWeight={600}>
        {field}
        {field.toLowerCase() === 'username' && (
          <span style={{ fontSize: 13, fontWeight: 400 }}>
            {' '}
            (not email address)
          </span>
        )}
        <span style={{ color: '#D5172F', fontWeight: 400 }}>*</span>
      </Typography>

      <InputBase
        {...props}
        ref={ref}
        sx={inputStyles}
        type={showPassword ? 'text' : type}
        error={!!error}
        endAdornment={
          type === 'password' && (
            <PasswordVisibility
              dataTestId={`show-${props.name}-button`}
              isVisible={showPassword}
              toggleVisiblity={() => setShowPassword(p => !p)}
            />
          )
        }
      />

      {field.toLowerCase() === 'password' && passwordStrength !== undefined && (
        <Stack
          width="100%"
          height={8}
          bgcolor="rgba(0, 0, 0, 0.2)"
          borderRadius={1}
        >
          <Stack
            alignItems="center"
            width={passwordStrengthWidths[passwordStrength] ?? 0}
            height={8}
            bgcolor={passwordStrengthColors[passwordStrength]}
            borderRadius={1}
            sx={{ transition: 'width 500ms ease-in-out' }}
          />
        </Stack>
      )}

      <ErrorMessage error={error} />
    </Stack>
  );
};

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  TextFieldComponent,
);

type TermsAndConditionsCheckboxProps = {
  checkboxProps: UseFormRegisterReturn;
};

const TermsAndConditionsCheckbox = ({
  checkboxProps,
}: TermsAndConditionsCheckboxProps) => {
  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="flex-start" gap={1.5}>
        <Box
          component="input"
          type="checkbox"
          width={14}
          height={14}
          my={0.375}
          sx={{ accentColor: colors.black, cursor: 'pointer' }}
          data-testid="terms-and-conditions-checkbox"
          {...checkboxProps}
        />
        <Typography variant="body2" fontSize={14}>
          I have read and accept the{' '}
          <Link
            href="https://cotality.com/au/legal/terms-conditions"
            target="_blank"
            color={colors.primary}
            data-testid="terms-and-conditions-link"
          >
            Terms and Conditions
          </Link>{' '}
          and{' '}
          <Link
            href="https://cotality.com/au/legal/privacy-policy"
            target="_blank"
            color={colors.primary}
          >
            Cotality's Privacy Policy
          </Link>
          .
        </Typography>
      </Stack>
    </Stack>
  );
};

const EMAIL_REGEX =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;
const USERNAME_REGEX = /^[-0-9a-zA-Z._]+$/;
const PASSWORD_REGEX = /([ -~]{8,128})/;

const schema = yup.object({
  firstName: yup
    .string()
    .required('First name is required.')
    .max(100, 'First name cannot be longer than 100 characters.')
    .trim(),
  lastName: yup
    .string()
    .required('Last name is required.')
    .max(100, 'Last name cannot be longer than 100 characters.')
    .trim(),
  emailAddress: yup
    .string()
    .required('Email address is required.')
    .max(100, 'Email address cannot be longer than 100 characters.')
    .matches(EMAIL_REGEX, 'Invalid email address.')
    .trim()
    .lowercase(),
  username: yup
    .string()
    .required('Username is required.')
    .min(3, 'Username must be atleast 3 characters.')
    .max(100, 'Username cannot be longer than 100 characters.')
    .matches(USERNAME_REGEX, 'Invalid username.')
    .trim(),
  password: yup
    .string()
    .required('Password is required.')
    .min(8, 'Password must be atleast 8 characters.')
    .max(128, 'Password cannot be longer than 128 characters.')
    .matches(PASSWORD_REGEX, 'Invalid password.'),
  confirmPassword: yup
    .string()
    .required('Confirm your password.')
    .oneOf([yup.ref('password')], 'Passwords must match.'),
  terms: yup
    .boolean()
    .required(
      'You must agree to our Terms and Conditions and Privacy Policy to proceed.',
    )
    .oneOf(
      [true],
      'You must agree to our Terms and Conditions and Privacy Policy to proceed.',
    ),
});

type FormData = yup.InferType<typeof schema>;

const defaultValues: FormData = {
  firstName: '',
  lastName: '',
  emailAddress: '',
  username: '',
  password: '',
  confirmPassword: '',
  terms: false,
};

export const SkilljarSignupPage = () => {
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const alert = useApi(alertApiRef);
  const config = useApi(configApiRef);
  const discovery = useApi(discoveryApiRef);
  const identity = useApi(identityApiRef);
  const estimate = useEstimatePassword();

  const { control, register, reset, setError, handleSubmit } =
    useForm<FormData>({
      defaultValues,
      mode: 'all',
      resolver: yupResolver(schema),
    });

  const { errors, dirtyFields } = useFormState({ control });

  const password = useWatch({ control, name: 'password' });
  const terms = useWatch({ control, name: 'terms' });

  const passwordStrength = useMemo(() => {
    if (!dirtyFields.password || password.length < 8) {
      return { score: -1, feedback: { warning: '' } };
    }

    return estimate(password);
  }, [estimate, password, dirtyFields.password]);

  useEffect(() => {
    if (password.length >= 8 && passwordStrength.score <= 1) {
      const warning = passwordStrength?.feedback.warning;
      let message = 'Password is too weak.';

      if (warning) {
        message += ` (${warning})`;
      }

      if (!errors.password) {
        setError('password', { message });
      }
    }
  }, [errors.password, password, passwordStrength, setError]);

  const signupMutation = useMutation({
    mutationFn: (body: CreateCLAUDUserRequest) =>
      signup(discovery, identity, body),
  });

  const SKILLJAR_URL = config.getOptionalString('skilljar.url');

  const formOnSubmit = handleSubmit(data => {
    const body: CreateCLAUDUserRequest = {
      ...data,
      redirectUri: SKILLJAR_URL,
      recaptchaToken,
    };

    signupMutation.mutate(body, {
      onSuccess: () => {
        alert.post({
          message: 'Successfully created the user.',
          severity: 'success',
          display: 'transient',
        });

        reset(defaultValues);

        if (SKILLJAR_URL) window.location.href = SKILLJAR_URL;
      },
      onError: e => {
        if (!isAxiosError(e)) {
          alert.post({
            message: 'Something went wrong when signing up.',
            severity: 'error',
            display: 'transient',
          });

          return;
        }

        const claudError = e.response?.data as CreateCLAUDUserError;
        const USERNAME_ALREADY_EXISTS = 'username.already.exists';
        const EMAIL_ALREADY_EXISTS = 'user.email.already.exists';

        switch (claudError.errors?.[0].key) {
          case EMAIL_ALREADY_EXISTS:
            setError('emailAddress', {
              message:
                'This email address is already registered. Please use a different one.',
            });
            break;

          case USERNAME_ALREADY_EXISTS:
            setError('username', {
              message:
                'This username is already taken. Please use a different one.',
            });
            break;

          default:
            alert.post({
              message: 'Something went wrong when signing up.',
              severity: 'error',
              display: 'transient',
            });
        }
      },
      onSettled: () => {
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      },
    });
  });

  return (
    <>
      <Helmet>
        <title>Cotality Campus | Sign Up</title>
        <meta name="description" content="Sign up for Campus Cotality." />
      </Helmet>
      <Stack
        flex={1}
        alignItems="center"
        sx={{
          backgroundImage: `url(${CotalityStarlings})`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'bottom',
        }}
      >
        <Stack
          width="100%"
          maxWidth={576}
          bgcolor="white"
          alignItems="center"
          gap={6}
          my={4}
          px={7}
          py={5}
          boxShadow="1px 0 16px -4px rgba(0, 0, 0, 0.25)"
        >
          <Stack textAlign="center">
            <Typography
              variant="h2"
              fontFamily="TWK Everett"
              fontSize={28}
              fontWeight={400}
            >
              Sign up for
            </Typography>
            <Typography
              variant="h2"
              fontFamily="TWK Everett"
              fontSize={36}
              fontWeight={600}
            >
              Cotality Campus
            </Typography>
          </Stack>

          <Stack
            component="form"
            width="100%"
            gap={1.5}
            onSubmit={formOnSubmit}
          >
            <TextField
              {...register('firstName')}
              field="First Name"
              placeholder="John"
              error={errors.firstName?.message}
            />

            <TextField
              {...register('lastName')}
              field="Last Name"
              placeholder="Doe"
              error={errors.lastName?.message}
            />

            <TextField
              {...register('emailAddress')}
              field="Email"
              placeholder="john.doe@example.com"
              error={errors.emailAddress?.message}
            />

            <TextField
              {...register('username')}
              field="Username"
              placeholder="john.doe"
              error={errors.username?.message}
            />

            <TextField
              {...register('password')}
              type="password"
              field="Password"
              placeholder="********"
              error={errors.password?.message}
              passwordStrength={passwordStrength.score as Score}
            />

            <TextField
              {...register('confirmPassword')}
              type="password"
              field="Confirm Password"
              placeholder="********"
              error={errors.confirmPassword?.message}
            />

            <Stack gap={4} mt={2}>
              <TermsAndConditionsCheckbox checkboxProps={register('terms')} />

              {config.getOptionalBoolean('recaptcha.enabled') && (
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={config.getOptionalString('recaptcha.siteKey') ?? ''}
                  onChange={value => setRecaptchaToken(value)}
                />
              )}

              <Button
                type="submit"
                variant="contained"
                sx={{
                  height: 40,
                  bgcolor: colors.primary,
                  ':hover': { bgcolor: colors.primaryHover },
                }}
                disabled={
                  !terms ||
                  (config.getOptionalBoolean('recaptcha.enabled') &&
                    !recaptchaToken)
                }
                data-testid="signup-button"
              >
                {signupMutation.isPending ? (
                  <CircularProgress
                    sx={{ color: 'white', marginLeft: 2 }}
                    size={16}
                  />
                ) : (
                  'Sign Up'
                )}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};
